import {useLocation} from 'wouter';

import {getAccount, getUserLocale} from '@mavismart_frontend/stores/account_store';
import {AgentContext} from '@mavismart_frontend/stores/agent_store';

/**
 * Extracts navigation context for the AI agent from the current route.
 * Never includes tokens, secrets, or cookies in the returned object.
 */
export function useAgentContext(): AgentContext {
  const [location] = useLocation();
  const account = getAccount();
  const locale = getUserLocale(account) ?? 'fr-FR';

  const {doorId, groupId, pageName} = parseRoute(location);

  return {pageName, route: location, doorId, groupId, locale};
}

export interface ParsedRoute {
  doorId?: string;
  groupId?: string;
  pageName: string;
}

/** @internal Exported for testing */
export function parseRoute(path: string): ParsedRoute {
  // /doors/:doorId (with optional sub-paths like /messages/:category)
  const doorMatch = path.match(/^\/doors\/([^/]+)/);
  if (doorMatch) {
    return {doorId: doorMatch[1], pageName: 'door_detail'};
  }

  // /groups/:groupId/doors/:doorId (door accessed from a group context)
  const groupDoorMatch = path.match(/^\/groups\/([^/]+)\/doors\/([^/]+)/);
  if (groupDoorMatch) {
    return {groupId: groupDoorMatch[1], doorId: groupDoorMatch[2], pageName: 'door_detail'};
  }

  // /groups/:groupId/collaborators or other sub-paths
  const groupSubMatch = path.match(/^\/groups\/([^/]+)\/(.+)/);
  if (groupSubMatch) {
    const groupId = groupSubMatch[1] ?? '';
    const subPath = groupSubMatch[2] ?? '';
    if (subPath === 'doors') {
      return {groupId, pageName: 'group_doors'};
    }
    return {groupId, pageName: `group_${subPath.split('/')[0]}`};
  }

  // /groups/:groupId (group detail)
  const groupMatch = path.match(/^\/groups\/([^/]+)$/);
  if (groupMatch) {
    return {groupId: groupMatch[1], pageName: 'group_detail'};
  }

  // /groups (group list)
  if (path === '/groups' || path === '/doors') {
    return {pageName: 'group_list'};
  }

  // /search/:doorId
  const searchDoorMatch = path.match(/^\/search\/([^/]+)/);
  if (searchDoorMatch) {
    return {doorId: searchDoorMatch[1], pageName: 'search'};
  }

  // /search
  if (path === '/search') {
    return {pageName: 'search'};
  }

  // /user/:page
  const userMatch = path.match(/^\/user\/([^/]+)/);
  if (userMatch) {
    return {pageName: `user_${userMatch[1]}`};
  }

  // /tracking
  if (path.startsWith('/tracking')) {
    return {pageName: 'tracking'};
  }

  // / (home)
  if (path === '/' || path === '') {
    return {pageName: 'home'};
  }

  // Fallback: derive page name from first path segment
  const segments = path.split('/').filter(Boolean);
  return {pageName: segments[0] ?? 'home'};
}
